/**
 * Recent + favorited ticker lists, persisted to localStorage.
 *
 * Two parallel lists shown next to the search box:
 *   • Recent — last N tickers selected, in MRU order. Auto-maintained
 *     whenever the user picks a symbol from search (or types one and
 *     hits enter). Capped at 5 — a 6th search drops the oldest.
 *   • Favorites — explicit star-toggle list, no cap. Stable order
 *     (insertion order, oldest first).
 *
 * Cross-tab sync via the standard `storage` event so a star toggled
 * in one tab shows up in another within ~50ms.
 *
 * The "always-on" equities pill row comes from settings.ticker_list
 * and is unrelated to this hook. This hook only tracks user-driven
 * discovery on top of the curated universe.
 */

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../lib/constants';

const RECENT_MAX = 5;

function readJSONList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string' && x.length > 0)
      .map(s => s.toUpperCase())
      .filter((s, i, arr) => arr.indexOf(s) === i);  // dedupe
  } catch {
    return [];
  }
}

function writeJSONList(key: string, list: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* storage quota / private-mode */
  }
}

export interface UseRecentTickersReturn {
  recent: string[];
  favorites: string[];
  /** Bump a symbol to the top of recent (called when the user picks
   *  one from search or clicks a recent chip). Always-on tickers (in
   *  the equities pill row) skip this so the recent list reflects
   *  discovered-via-search choices, not every click. */
  pushRecent: (symbol: string) => void;
  /** Remove a symbol from the recent list only. Favorites are left
   *  untouched — the user can un-favorite separately via toggleFavorite.
   *  The write to localStorage fires the cross-tab storage event so
   *  other open tabs sync within ~50 ms. */
  removeRecent: (symbol: string) => void;
  /** Toggle a symbol's favorite status. */
  toggleFavorite: (symbol: string) => void;
  /** Whether a symbol is favorited (handy for star icons). */
  isFavorite: (symbol: string) => boolean;
}

export function useRecentTickers(): UseRecentTickersReturn {
  const [recent, setRecent] = useState<string[]>(() => readJSONList(STORAGE_KEYS.recentTickers));
  const [favorites, setFavorites] = useState<string[]>(() => readJSONList(STORAGE_KEYS.favoriteTickers));

  // Cross-tab sync: when another tab writes either list, mirror it
  // here without re-writing (would cause a feedback loop).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.recentTickers) {
        setRecent(readJSONList(STORAGE_KEYS.recentTickers));
      } else if (e.key === STORAGE_KEYS.favoriteTickers) {
        setFavorites(readJSONList(STORAGE_KEYS.favoriteTickers));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const pushRecent = useCallback((rawSymbol: string) => {
    const symbol = rawSymbol.toUpperCase();
    if (!symbol) return;
    setRecent(prev => {
      const next = [symbol, ...prev.filter(s => s !== symbol)].slice(0, RECENT_MAX);
      writeJSONList(STORAGE_KEYS.recentTickers, next);
      return next;
    });
  }, []);

  const toggleFavorite = useCallback((rawSymbol: string) => {
    const symbol = rawSymbol.toUpperCase();
    if (!symbol) return;
    setFavorites(prev => {
      const next = prev.includes(symbol)
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol];
      writeJSONList(STORAGE_KEYS.favoriteTickers, next);
      return next;
    });
  }, []);

  const removeRecent = useCallback((rawSymbol: string) => {
    const symbol = rawSymbol.toUpperCase();
    if (!symbol) return;
    setRecent(prev => {
      const next = prev.filter(s => s !== symbol);
      writeJSONList(STORAGE_KEYS.recentTickers, next);
      return next;
    });
  }, []);

  const isFavorite = useCallback(
    (symbol: string) => favorites.includes(symbol.toUpperCase()),
    [favorites],
  );

  return { recent, favorites, pushRecent, removeRecent, toggleFavorite, isFavorite };
}
